import { FormCheckbox, FormDatePicker, FormInput, FormMonthRangePicker, FormSelect } from "@/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { get, useFieldArray, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { actionsKeys, allOption, drcrOptions, permissionActions, transactionTypeOptions } from "@/lib/utils";
import useApi from "./useApi";
import { toast } from "@/components/Toast";
import { useGetAuditTrailById, useGetDocumentTypeDropdown, useGetGLConfig, useResubmitAuditFormMutation } from "@/services";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import moment from "moment";
import { 
  VENDOR_ID,
  PAYMENT_TERM_ID,
  BILLING_CATEGORY,
  ZONE_ID,
  BLOCK_ID,
  PROPERTY_ID,
  PROPERTY_OWNERSHIP_ID,
  GL_MASTER,
  TRANSACTION_TYPE,
  DRCR,
  LINE_ITEM_TYPE,
  BUDGET_YEAR,
  BUDGET_START_MONTH,
  BUDGET_END_MONTH,
  BALANCE_AMOUNT,
  isFieldSkipped,
  POSTING_DATE,
  POSTING_YEAR,
  POSTING_PERIOD,
  DUE_DATE,
  UNITS,
  RATE,
  AMOUNT,
  HEADER,
  LINE_ITEM,
  HEADER_PAYLOAD,
  LINE_ITEM_PAYLOAD,
  CURRENCY,
  ALT_GL_MASTER,
  BANK_ACC_GL,
  BANK_ACC_NAME,
  DIST_ZONE,
  DIST_BLOCK,
  DIST_PROPERTY,
 } from "./consts";
import { FAIL_STATUS } from "../../consts";
import { routes } from "@/routes/utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setAuditTrailDtl } from "@/redux/slices";

const useManualEntry = () => {
  const { id } = useParams();
  const location = useLocation();
  const { ujeAuditId } = location.state || {};
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const auditTrailDtl = useSelector((state: RootState) => state.currentData.auditTrailDtl);
  const accessPermission = useSelector((state: RootState) => state.currentData.accessPermission);
  const [documentTypeId, setDocumentTypeId] = useState<string>("");
  const [activeAuditId, setActiveAuditId] = useState<string>(ujeAuditId);
  

  const { data: _auditTrailData, isLoading: isLoadingAuditData } = useGetAuditTrailById({
    communityId: communityData?.communityId,
    jeMasterId: id as string,
    ujeAuditId: activeAuditId,
    queryParams: {
      enabled: !!(id && communityData?.communityId && activeAuditId)
    }
  })

  const auditTrailData = _auditTrailData?.data;

  const hasEditPermission = useMemo(() => accessPermission?.[actionsKeys?.AUDIT_TRAIL?.toLowerCase()]?.[permissionActions.UPDATE], [accessPermission]);
  
  useEffect(() => {
    if (JSON.stringify(auditTrailDtl) != JSON.stringify(auditTrailData)) {
      dispatch(setAuditTrailDtl(auditTrailData))
    }
  }, [auditTrailData])
  
  const {data: documentTypeList, isLoading: isListLoading} = useGetDocumentTypeDropdown({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  });
  
  const {data: _configFields, isLoading: isRefetchingConfigFields } = useGetGLConfig({
    communityId: communityData?.communityId,
    documentTypeId: documentTypeId,
    queryParams: {
      enabled: !!(communityData?.communityId && documentTypeId)
    }
  });

  const configFields = _configFields?.data;

  useEffect(() => {
    if (auditTrailData && documentTypeList) {
      const docTypeId = documentTypeList?.find((type: any) => type.documentType == auditTrailData?.documentType)?.documentTypeId
  
      if (docTypeId != documentTypeId) {
        setDocumentTypeId(docTypeId)
      }
    }
  }, [documentTypeList, auditTrailData])

  const {
    vendorList,
    paymentTermOptions,
    billingCategoryList,
    zoneList,
    blockList,
    propertyList,
    propertyOccupancyList,
    glData,
    glAccount
  } = useApi({configFields});

  const fieldDisabled = useMemo(() => {
    return auditTrailData?.processingStatus != FAIL_STATUS
  }, [auditTrailData]);

  const headerFields = useMemo(() => configFields?.filter(
    (f: any) => f?.fieldLevel?.toLowerCase() == "header" && f.applicable
  ) || [], [configFields]);

  const lineItemFields = useMemo(() => configFields?.filter(
    (f: any) => f?.fieldLevel?.toLowerCase() == "line_item" && f.applicable
  ) || [], [configFields]);

  const getOptions = (key: string, index?: number) => {
    switch (key) {
      case VENDOR_ID:
        return (vendorList || [])?.map((data: any) => ({ label: data?.vendorName, value: data?.vendorId?.toString() }));

      case PAYMENT_TERM_ID:
        return (paymentTermOptions?.data || [])?.map((data: any) => ({
          value: data?.paymentTermId?.toString(),
          label: data?.paymentTermName,
        }))

      case BILLING_CATEGORY:
        return (billingCategoryList?.data || [])?.map((data: any) => ({
          value: data?.billingCategoryId?.toString(),
          label: data?.categoryName,
        }))

      case TRANSACTION_TYPE:
        return transactionTypeOptions;
      
      case ZONE_ID:
        return (zoneList?.data || [])?.map((data: any) => ({
          value: data?.zoneId?.toString(),
          label: data?.zoneName,
        }))

      case BLOCK_ID:
        return (blockList?.data || [])?.map((data: any) => ({
          value: data?.blockId?.toString(),
          label: data?.blockName,
        }))

      case PROPERTY_ID:
        return (propertyList?.data || [])?.map((data: any) => ({
          value: data?.propertyId?.toString(),
          label: data?.name,
        }))

      case DIST_ZONE:
        return [...allOption, ...(zoneList?.data || [])
          ?.map((data: any) => ({
            value: data?.zoneId?.toString(),
            label: data?.zoneName,
          }))
        ]

      case DIST_BLOCK:
        return [...allOption, ...(blockList?.data || [])
        ?.filter((data: any) => form.watch(`${LINE_ITEM}.${index}.${DIST_ZONE}` as any) == "all" || data.zoneId?.toString() == form.watch(`${LINE_ITEM}.${index}.${DIST_ZONE}` as any))
        ?.map((data: any) => ({
          value: data?.blockId?.toString(),
          label: data?.blockName,
        }))]

      case DIST_PROPERTY:
        return [...allOption, ...(propertyList?.data || [])
        ?.filter((data: any) => form.watch(`${LINE_ITEM}.${index}.${DIST_BLOCK}` as any) == "all" || data.blockId?.toString() == form.watch(`${LINE_ITEM}.${index}.${DIST_BLOCK}` as any))
        ?.map((data: any) => ({
          value: data?.propertyId?.toString(),
          label: data?.name,
        }))]
        
      case DRCR:
        return drcrOptions
      
      case PROPERTY_OWNERSHIP_ID:
        return (propertyOccupancyList?.data || [])?.map((data: any) => ({
          value: data?.occupancyId?.toString(),
          label: `${data?.firstName} ${data?.lastName}`,
        }))

      case GL_MASTER:
        return (glAccount || [])?.map((data: any) => ({
          label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
          value: data?.glMasterId?.toString(),
          disabled: !glData?.find((gl: any) => gl?.glMasterId?.toString() == data?.glMasterId?.toString())
        }))

      case BANK_ACC_GL:
        return (glAccount || [])?.map((data: any) => ({
          label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
          value: data?.glMasterId?.toString(),
          disabled: !glData?.find((gl: any) => gl?.glMasterId?.toString() == data?.glMasterId?.toString())
        }));

      case ALT_GL_MASTER:
        return (glAccount || [])?.map((data: any) => ({
          label: `${data?.glAccountNumber}`,
          value: data?.glMasterId?.toString(),
          disabled: !glData?.find((gl: any) => gl?.glMasterId?.toString() == data?.glMasterId?.toString())
        }))

      case LINE_ITEM_TYPE:
        return [{label: 'Line Item 1', value: '1'}];

      default:
        return [];
    }
  }

  const generateSchema = (fields: any[]) => {
    const schemaShape: any = {};
    fields?.forEach((field) => {
      if (isFieldSkipped(field.fieldName)) return false;
      if (field.fieldName == "balanceAmount") return false;
      let validator: any = z.any();
      
      if (["STRING", "INTEGER", "DECIMAL", "ENUM", "LOOKUP"].includes(field.fieldDataType)) {
        validator = z.string();
        validator = field.required 
          ? validator.min(1, { message: `This field is required` }) 
          : validator.optional().or(z.literal(""));
      }

      if (field.fieldName == BUDGET_YEAR) validator = z.any();
      schemaShape[field.fieldName] = validator;
    });
    return schemaShape;
  };

  const formSchema = useMemo(() => {
    return z.object({
      // Wrap header fields in their own object schema
      [HEADER]: z.object(generateSchema(headerFields)),
      [LINE_ITEM]: z.array(z.object(generateSchema([...lineItemFields,  {"fieldName": "lineItemType"}])))
    });
  }, [headerFields, lineItemFields]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      [HEADER]: headerFields.reduce((acc: any, field: any) => {
        acc[field.fieldName] = field.defaultValue || "";
        return acc;
      }, {}),
      [LINE_ITEM]: lineItemFields.length ? [lineItemFields.reduce((acc: any, field: any) => {
        acc[field.fieldName] = field.defaultValue || "";
        return acc;
      }, {})] : []
    }
  });

  useEffect(() => {
    if (configFields && auditTrailData) {
      // 1. Prepare Header Data
      const headerData = headerFields.reduce((acc: any, field: any) => {
        // Map auditTrailData values to header fields, fallback to default value
        let value = auditTrailData[HEADER_PAYLOAD]?.[field.fieldName]?.toString() ?? "";

        if (field.fieldName == POSTING_PERIOD) {
          value = auditTrailData?.[HEADER_PAYLOAD]?.[POSTING_DATE]
          ? moment(auditTrailData?.[HEADER_PAYLOAD]?.[POSTING_DATE]).format("MMM")
          : ""
        } else if (field.fieldName == POSTING_YEAR) {
          value = auditTrailData?.[HEADER_PAYLOAD]?.[POSTING_DATE]
          ? moment(auditTrailData?.[HEADER_PAYLOAD]?.[POSTING_DATE]).format("YYYY")
          : ""
        } else if (field.fieldName == CURRENCY) {
          value = 'USD';
        }

        acc[field.fieldName] = value ?? "";
        return acc;
      }, {});
  
      // 2. Prepare Line Item Data
      const lineItemData = auditTrailData[LINE_ITEM_PAYLOAD]?.map((item: any) => {
        
        return lineItemFields.reduce((acc: any, field: any) => {
          let value = item[field.fieldName]?.toString() ?? "";

          if (field.fieldName == BUDGET_YEAR && item?.[BUDGET_YEAR]) {
            value = {
              from: new Date(
                item?.[BUDGET_YEAR],
                Number(item?.[BUDGET_START_MONTH] - 1),
                1
              ),
              to: new Date(
                item?.[BUDGET_YEAR],
                Number(item?.[BUDGET_END_MONTH] - 1),
                1
              ),
            }
          } else if (field.fieldName == AMOUNT && item[UNITS] && item[RATE]) {
            value = (Number(item[UNITS]) * Number(item[RATE]))?.toString();
          } else if (field.fieldName == ALT_GL_MASTER) {
            value = item[GL_MASTER]?.toString()
          } else if (field.fieldName == PROPERTY_OWNERSHIP_ID) {
            value = item[PROPERTY_OWNERSHIP_ID]?.toString()
          }

          acc[field.fieldName] = value ?? "";
          return acc;
        }, {...item});
      }) || [];
  
      // 3. Reset form with mapped data
      form.reset({
        [HEADER]: headerData,
        [LINE_ITEM]: lineItemData.length > 0 ? lineItemData : (lineItemFields.length ? [{}] : [])
      });
    }
  }, [configFields, auditTrailData, form, headerFields, lineItemFields]);

  const onChangeText = (index: number, fieldName: string) => (e: any) => {
    const fName = index !== undefined 
      ? `${LINE_ITEM}.${index}.${fieldName}` 
      : `${HEADER}.${fieldName}`;
      
    form.setValue(fName as any, e.target.value);

    if (fieldName == UNITS || fieldName == RATE) {
      calculateAmount(
        fieldName == UNITS ? e.target.value : form.watch(`${LINE_ITEM}.${index}.${UNITS}` as any),
        fieldName == RATE ? e.target.value : form.watch(`${LINE_ITEM}.${index}.${RATE}` as any),
        `${LINE_ITEM}.${index}.${AMOUNT}`
      );
    }
  }

  const calculateAmount = (unit: number, rate: number, amountKey: string) => {
    const amt = unit * rate;
    form.setValue(amountKey as any, parseFloat(amt.toString()).toFixed(2));
  };

  // const lineItems = useWatch({ control: form.control, name: LINE_ITEM });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: LINE_ITEM
  });

  const watchedLineItems = useWatch({
    control: form.control,
    name: LINE_ITEM, 
  });

  const balanceAmount = useMemo(() => {
    if (!Array.isArray(watchedLineItems)) return 0;

    // 2. Optimized single-pass calculation
    const totals = watchedLineItems?.reduce((acc, item) => {
      const amt = Number(item?.amount) || 0;
      const type = item?.drCr?.toLowerCase();

      if (type == "dr") acc.dr += amt;
      if (type == "cr") acc.cr += amt;
      
      return acc;
    }, { dr: 0, cr: 0 });

    return totals.dr - totals.cr;
    
    // 3. Dependency array is fine, but lineItems MUST be from form.watch()
  }, [watchedLineItems]);

  const onChangeDate = () => {
    if (configFields?.[0]?.documentType == "VI") {
      onBaseLineDateChanged();
      onPostingDateChanged();
    }
  }

  const onBaseLineDateChanged = () => {
    const { header } = form.watch();
    const { baselineDate, vendorId } = header;

    const days = paymentTermOptions?.data?.find((term: any) => {
      const _paymentTerm = (vendorList || [])
        .find(
          (vendor: any) => vendor.vendorId?.toString() === vendorId?.toString()
        )
        ?.paymentTerm?.toString();

      return term?.paymentTermId?.toString() == _paymentTerm;
    })?.days;

    if (baselineDate && days) {
      form.setValue(`${HEADER}.${DUE_DATE}`, moment(baselineDate).add(days, "days"));
    }
  };

  const onPostingDateChanged = () => {
    const { header } = form.watch();
    const { postingDate } = header;

      form.setValue(`${HEADER}.${POSTING_YEAR}`, moment(postingDate).format('YYYY'));
      form.setValue(`${HEADER}.${POSTING_PERIOD}`, moment(postingDate).format('MMM'));
  };

  const onOptionChange = (index: number, fieldName: string) => (e: any)=> {
    const fName = index !== undefined 
      ? `${LINE_ITEM}.${index}.${fieldName}` 
      : `${HEADER}.${fieldName}`;
    
    form.setValue(fName as any, e);

    if (fieldName == GL_MASTER) {
      const altNo = glAccount?.find((data: any) => e == data?.glMasterId?.toString())?.alternativeAccount?.toString()
      form.setValue(
        `${LINE_ITEM}.${index}.${ALT_GL_MASTER}`,
        glAccount?.find((gl: any) => gl?.glAccountNumber?.toString() == altNo?.toString())?.glMasterId?.toString()
      )
    } else if (fieldName == BANK_ACC_GL) {
      form.setValue(`${HEADER}.${BANK_ACC_NAME}`, glAccount?.find((data: any) => data.glMasterId == e)?.accountShortDesc || '')
    }
  }

  const renderDynamicField = (field: any, form: any, index?: number) => {
    // If index is provided, it's a line item, so we prefix the name
    const fieldName = index !== undefined 
      ? `${LINE_ITEM}.${index}.${field.fieldName}` 
      : `${HEADER}.${field.fieldName}`; // Path for header fields

    const {errors} = form.formState;
    const error = get(errors, fieldName);

    const commonProps = {
      name: fieldName,
      control: form.control,
      label: index !== undefined ? "" : field.displayLabel, // Hide labels in table rows usually
      isRequired: field.required,
      disabled: !field.editable || field.fieldName == CURRENCY || field.fieldName == ALT_GL_MASTER || fieldDisabled,
      error: error?.message
    };

    const isAmount = field?.fieldName?.toLowerCase()?.includes("amount");

    if (field.fieldName?.toLowerCase() == BUDGET_YEAR?.toLowerCase()) {
      const hasBudgetMonthStart = configFields?.some(
          (f: any) => f.fieldName?.toLowerCase() === BUDGET_START_MONTH?.toLowerCase() && f.applicable
        );
      const hasBudgetMonthEnd = configFields?.some(
          (f: any) => f.fieldName?.toLowerCase() === BUDGET_END_MONTH?.toLowerCase() && f.applicable
        );
      const allExists = hasBudgetMonthEnd && hasBudgetMonthStart;
        
      return allExists ? <FormMonthRangePicker {...commonProps} placeholder="Please select"/> : <></>
    }

    switch (field.fieldDataType?.toUpperCase()) {
      case "INTEGER":
        if (field.fieldName == DIST_ZONE || field.fieldName == DIST_BLOCK || field.fieldName == DIST_PROPERTY) {
          let options = [];
          try { 
            options = getOptions(field.fieldName, index); 
          } catch { 
            options = []; 
          }
          return <FormSelect {...commonProps} options={options} placeholder="Please select" onChange={onOptionChange(index as number, field?.fieldName)} />;
        }
        return <FormInput {...commonProps} variant="integer" placeholder="Please enter" startIcon={isAmount ? "$" : ""} onChange={onChangeText(index as number, field?.fieldName)} />;
      case "DECIMAL":
        return <FormInput {...commonProps} variant="float" placeholder="Please enter" startIcon={isAmount ? "$" : ""} onChange={onChangeText(index as number, field?.fieldName)} />;
      case "DATE":
        return <FormDatePicker {...commonProps} placeholder="Please select" onChange={onChangeDate} />;
      case "BOOLEAN":
        return <FormCheckbox {...commonProps} placeholder="Please select"/>;
      case "ENUM":
      case "LOOKUP":
        let options = [];
        try { 
          options = getOptions(field.fieldName, index); 
        } catch { 
          options = []; 
        }
        return <FormSelect {...commonProps} options={options} placeholder="Please select" onChange={onOptionChange(index as number, field?.fieldName)}/>;
      default:
        return <FormInput {...commonProps} placeholder="Please enter" onChange={onChangeText(index as number, field?.fieldName)} />;
    }
  };

  const addRow = () => {
    const emptyRow = lineItemFields.reduce((acc: any, field: any) => {
      acc[field.fieldName] = field.defaultValue || "";
      return acc;
    }, {});
    append(emptyRow);
  };

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}/${routes.audit}/${routes.auditTrail}`)
  }

  const {
    mutate: resubmitUJE,
    isPending: isAddPending
  } = useResubmitAuditFormMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: ({data}: any) => {
      if (data?.data?.errors) {
        const msg = data?.data?.errors?.map((err: any) => `${err.fieldCode ? `${err.fieldCode}: ` : ''}${err.errorMessage}`)
        toast.error(msg
        );
      } else {
        onBackClick();
        toast.success("Form Resubmitted Successfully");
      }
    },
  });

  const onSubmit = (data: any) => {
    Object.keys(data[HEADER])?.map((key: any) => {
      if (key?.toLowerCase()?.includes("date")) {
        data[HEADER][key] = data.header[key] ? moment(data.header[key]).format('YYYY-MM-DD') : null
      }

      if (key == 'postingPeriod') {
        data[HEADER][key] = data.header[POSTING_DATE] ? moment(data.header[POSTING_DATE]).format('MM') : null
      }
    })
    data[HEADER][BALANCE_AMOUNT] = balanceAmount;

    data[LINE_ITEM]?.map((item: any, index: number) => {
      Object.keys(item)?.map((key: any) => {
        if (key?.toLowerCase()?.includes("date")) {
          data[LINE_ITEM][index][key] = data[LINE_ITEM][index][key] ? moment(data[LINE_ITEM][index][key]).format('YYYY-MM-DD') : null
        }

        if (key?.toLowerCase() == DIST_ZONE?.toLowerCase()) {
          data[LINE_ITEM][index][DIST_ZONE] = data[LINE_ITEM][index][DIST_ZONE] == "all" ? "" : data[LINE_ITEM][index][DIST_ZONE];
        }

        if (key?.toLowerCase() == DIST_BLOCK?.toLowerCase()) {
          data[LINE_ITEM][index][DIST_BLOCK] = data[LINE_ITEM][index][DIST_BLOCK] == "all" ? "" : data[LINE_ITEM][index][DIST_BLOCK];
        }

        if (key?.toLowerCase() == DIST_PROPERTY?.toLowerCase()) {
          data[LINE_ITEM][index][DIST_PROPERTY] = data[LINE_ITEM][index][DIST_PROPERTY] == "all" ? "" : data[LINE_ITEM][index][DIST_PROPERTY];
        }

        if (key?.toLowerCase() == BUDGET_YEAR?.toLowerCase()) {
          const budgetMonth = data[LINE_ITEM][index][BUDGET_YEAR];

          data[LINE_ITEM][index][BUDGET_YEAR] = budgetMonth
            ? moment(budgetMonth?.from).format("YYYY")
            : null;
          data[LINE_ITEM][index][BUDGET_START_MONTH] = budgetMonth
            ? moment(budgetMonth?.from).format("MM")
            : null;
            data[LINE_ITEM][index][BUDGET_END_MONTH] = budgetMonth
            ? moment(budgetMonth?.to).format("MM")
            : null;
        }
      })
      // data[LINE_ITEM][index][LINE_ITEM_TYPE] = "MAIN";
    })

    resubmitUJE({
      communityId: communityData?.communityId,
      jeMasterId: auditTrailData?.jeMasterId,
      ujeAuditId: auditTrailData?.ujeAuditId,
      data: {
        previousJeMasterId: auditTrailData?.jeMasterId,
        previousUjeAuditId: auditTrailData?.ujeAuditId,
        ...data, 
      }
    })
  }

  const nextStageId = useMemo(() => auditTrailData?.next?.ujeAuditId ,[auditTrailData]);
  const prevStageId = useMemo(() => auditTrailData?.previous?.ujeAuditId ,[auditTrailData]);

  const onPrev = () => {
    if (prevStageId) setActiveAuditId(prevStageId);
  }

  const onNext = () => {
    if (nextStageId) setActiveAuditId(nextStageId);
  }

  const hideLineItems = useMemo(() => auditTrailData?.stage == "SUBLEDGER_CUSTOMER" || auditTrailData?.stage == "SUBLEDGER_VENDOR", [auditTrailData]);

  return {
    form,
    renderDynamicField,
    headerFields,
    lineItemFields: lineItemFields.length ? [...lineItemFields, {fieldName: 'delete'}] : [],
    lineItemRows: fields,
    addRow,
    removeRow: remove,
    isAddPending,
    onSubmit,
    balanceAmount,
    isRefetchingConfigFields,
    fieldDisabled,
    isListLoading,
    nextStageId,
    prevStageId,
    onPrev,
    onNext,
    isLoadingAuditData,
    hideLineItems,
    isNextJeMasterIdExists: !!auditTrailData?.nextJeMasterId,
    hasEditPermission
  };
};

export default useManualEntry;